import { resolveId } from '@medplum/core'
import type {
  Organization,
  Patient,
  Practitioner,
  Reference,
  Task,
} from '@medplum/fhirtypes'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { codeFromCodeableConcept } from '#src/codeableConcept.ts'
import { Extension } from '#src/decorators.ts'
import { ExtendedResource, type ExportFields } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import { isOfType, now } from '#src/utils.ts'

export abstract class ExtendedTask<TState> extends ExtendedResource<Task> {
  static readonly RESOURCE_TYPE = 'Task'
  constructor(params: Omit<Task, 'resourceType'>) {
    super({
      authoredOn: now(),
      lastModified: now(),
      ...params,
    })
  }

  set startDate(startDate: string | undefined) {
    this.resource.executionPeriod ??= {}
    if (this.resource.executionPeriod.end) {
      delete this.resource.executionPeriod.end
    }
    this.resource.executionPeriod.start = startDate
    this.resource.lastModified = now()
  }

  get startDate(): string | undefined {
    return this.resource.executionPeriod?.start
  }

  get completionDate(): Date | undefined {
    return this.resource.executionPeriod?.end
      ? new Date(this.resource.executionPeriod.end)
      : undefined
  }

  set completionDate(completionDate: string | undefined) {
    this.resource.executionPeriod ??= {}
    this.resource.executionPeriod.end = completionDate
    this.resource.lastModified = now()
  }

  protected abstract mapStateToTaskFields(_state: TState): {
    businessStatus: Task['businessStatus']
    intent: Task['intent']
    status: Task['status']
  }

  get businessStatus(): Task['businessStatus'] {
    return this.resource.businessStatus
  }

  get code(): Task['code'] {
    return this.resource.code
  }

  set code(code: Task['code']) {
    this.resource.code = code
  }

  get state(): TState {
    return codeFromCodeableConcept(this.resource.businessStatus) as TState
  }

  set state(state: TState) {
    const { businessStatus, intent, status } = this.mapStateToTaskFields(state)
    const now = new Date()
    this.resource.businessStatus = businessStatus
    this.resource.intent = intent
    this.resource.status = status
    this.resource.lastModified = now.toISOString()
  }

  delete(): void {
    this.resource.status = 'entered-in-error'
  }

  get isDeleted(): boolean {
    return this.resource.status === 'entered-in-error'
  }

  get title(): string {
    if (this.isDeleted) {
      return '[deleted]'
    }
    // https://www.hl7.org/fhir/task.html#titles
    return this.resource.code?.text ?? ''
  }

  set title(title: string | null | undefined) {
    // Copy rather than mutate: `code` may be a shared constant such as
    // StandardTask.code, which every new task starts with.
    this.resource.code = { ...this.resource.code, text: title ?? undefined }
    this.resource.lastModified = now()
  }

  get description(): string | undefined {
    if (this.isDeleted) {
      return '[deleted]'
    }
    return this.resource.description
  }

  set description(description: string | undefined) {
    this.resource.description = description
    this.resource.lastModified = now()
  }

  get note(): Task['note'] {
    return this.resource.note
  }

  set note(note: Task['note']) {
    this.resource.note = note
    this.resource.lastModified = now()
  }

  static prioritySchema = z.enum(['routine', 'urgent', 'asap', 'stat'])

  get priority(): Task['priority'] {
    return this.resource.priority
  }

  get priorityText(): string | undefined {
    return ExtendedTask.priorityText(this.priority)
  }

  static priorityText(priority: Task['priority']): string | undefined {
    return match(priority)
      .with('routine', () => 'Low')
      .with('asap', () => 'Medium')
      .with('urgent', () => 'High')
      .with('stat', () => 'STAT')
      .with(undefined, () => undefined)
      .exhaustive()
  }

  get priorityNumber(): number | undefined {
    return match(this.priority)
      .with('stat', () => 0)
      .with('urgent', () => 1)
      .with('asap', () => 2)
      .with('routine', () => 3)
      .with(undefined, () => undefined)
      .exhaustive()
  }

  get isStatPriority(): boolean {
    return this.priority === 'stat'
  }

  set priority(priority: Task['priority']) {
    this.resource.priority = priority
  }

  get authoredOn(): string {
    invariant(
      this.resource.authoredOn,
      `${this.reference.reference} must have an authoredOn date`
    )
    return this.resource.authoredOn
  }

  get lastModified(): string | undefined {
    return this.resource.lastModified
  }

  get lastModifiedDate(): Date | undefined {
    return this.resource.lastModified
      ? new Date(this.resource.lastModified)
      : undefined
  }

  get dueOn(): string | undefined {
    return this.resource.restriction?.period?.end
  }

  set dueOn(dueOn: string | undefined) {
    this.resource.restriction ??= {}
    this.resource.restriction.period ??= {}
    this.resource.restriction.period.end = dueOn
    this.resource.lastModified = now()
  }

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/due-on-knock-schedule-id',
    kind: 'valueString',
  })
  dueOnKnockScheduleId?: string | undefined

  get owner(): Reference<Practitioner | Organization> | undefined {
    invariant(
      this.resource.owner === undefined ||
        isOfType(this.resource.owner, 'Practitioner') ||
        isOfType(this.resource.owner, 'Organization'),
      `${this.reference.reference} owner must be a Practitioner or Organization reference`
    )
    return this.resource.owner
  }

  set owner(owner: Reference<Practitioner | Organization> | undefined) {
    this.resource.owner = owner
    this.resource.lastModified = now()
  }

  get ownerPractitioner(): Reference<Practitioner> | undefined {
    return isOfType(this.resource.owner, 'Practitioner')
      ? this.resource.owner
      : undefined
  }

  set ownerPractitioner(owner: Reference<Practitioner> | undefined) {
    this.owner = owner
  }

  get ownerOrganization(): Reference<Organization> | undefined {
    return isOfType(this.resource.owner, 'Organization')
      ? this.resource.owner
      : undefined
  }

  set ownerOrganization(owner: Reference<Organization> | undefined) {
    this.owner = owner
  }

  get requester(): Task['requester'] {
    return this.resource.requester
  }

  get requesterPractitioner(): Reference<Practitioner> | undefined {
    return isOfType(this.resource.requester, 'Practitioner')
      ? this.resource.requester
      : undefined
  }

  set requester(requester: Task['requester']) {
    this.resource.requester = requester
    this.resource.lastModified = now()
  }

  get author(): Reference<Practitioner> | undefined {
    if (this.resource.requester) {
      invariant(
        isOfType(this.resource.requester, 'Practitioner'),
        `${this.reference.reference} requester must be a Practitioner reference when accessing author`
      )
    }
    return this.resource.requester
  }

  get participant(): Reference<Patient> | undefined {
    const participant = this.resource.for
    if (!participant) {
      return undefined
    }
    invariant(
      isOfType(participant, 'Patient'),
      `${this.reference.reference} participant must be a Patient reference`
    )
    return participant
  }

  set participant(participant) {
    this.resource.for = participant
  }

  get focus(): Reference | undefined {
    return this.resource.focus
  }

  set focus(focus: Reference | undefined) {
    this.resource.focus = focus
  }

  get basedOn(): Reference[] {
    return this.resource.basedOn ?? []
  }

  set basedOn(basedOn: Reference[]) {
    if (basedOn.length === 0) {
      this.resource.basedOn = undefined
    } else {
      this.resource.basedOn = basedOn
    }
  }

  protected getExportFields(): ExportFields {
    return {
      id: this.id,
      title: this.title,
      description: this.description,
      status: this.resource.status,
      code: codeFromCodeableConcept(this.code),
      businessStatus: codeFromCodeableConcept(this.businessStatus),
      priority: this.priority,
      authoredOn: this.authoredOn,
      lastModified: this.lastModified,
      dueOn: this.dueOn,
      completionDate: this.completionDate,
      patientId: resolveId(this.participant),
      ownerId: resolveId(this.ownerPractitioner),
      ownerPractitionerId: resolveId(this.ownerPractitioner),
      ownerOrganizationId: resolveId(this.ownerOrganization),
      requesterId: resolveId(this.requester),
      focusId: resolveId(this.focus),
    }
  }
}
